import { type FC, useMemo } from 'react';

import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { getFlightTypeText } from '@vakantiesnl/components/src/molecules/Flight';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { getTimeFromDate } from '@vakantiesnl/services/src/util/dateUtils';
import { type Search, type Booking } from '@vakantiesnl/types';
import dayjs from 'dayjs';

import { useCustomStyles } from './FlightInfo.styles';

export type FlightInfoProps = {
	isOutbound: boolean;
	className?: string;
	dataCy?: string;
	customLabel?: string;
} & (
	| {
			showFlightDetails: false;
			flightDate: Booking.Flight['departureDateTime'];
			flight?: never;
	  }
	| {
			showFlightDetails: true;
			flight: Search.Flight | Booking.Flight | Booking.FlightSegment;
			flightDate?: never;
	  }
);

export const FlightInfo: FC<FlightInfoProps> = ({
	flight,
	flightDate,
	isOutbound,
	className,
	dataCy,
	showFlightDetails,
	customLabel,
}) => {
	const { classes, cx } = useCustomStyles();
	const microCopies = useMicroCopyContext();

	const labelText = useMemo(
		() => (isOutbound ? microCopies['common.departure'] : microCopies['common.return']),
		[isOutbound, microCopies],
	);

	return (
		<div className={cx(classes.container, className)} data-cy={dataCy}>
			<Typography variant="labelSm" as="span">
				{customLabel ?? labelText}
			</Typography>
			<Typography variant="bodyLg" as="p">
				{dayjs(flightDate ?? flight.departureDateTime).format('dd D MMM YYYY')}
			</Typography>
			{showFlightDetails && (
				<>
					<Typography variant="bodySm" as="p" className={classes.caption}>
						{getTimeFromDate(new Date(flight.departureDateTime))} ({flight.departureAirport.code}) /{' '}
						{getTimeFromDate(new Date(flight.arrivalDateTime))} ({flight.arrivalAirport.code})
					</Typography>
					<Typography variant="bodySm" as="p" className={cx(classes.caption, classes.flightType)}>
						{'stopOver' in flight && getFlightTypeText(flight, microCopies)}
					</Typography>
					<Typography variant="bodySm" as="p" className={classes.caption}>
						{flight.airline?.value} {flight.displayFlightNumber}
					</Typography>
				</>
			)}
		</div>
	);
};
